<script setup lang="ts">
/**
 * ConvItem - 对话列表项 (收藏夹 + 历史对话共用).
 * 头像 + 标题 + 时间 + hover 操作按钮 (收藏/重命名/删除).
 */
import { StarIcon as StarOutlineIcon, PencilIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/vue/24/solid'
import { formatRelativeTime } from '@/utils/format'
import RoleAvatar from '@/components/common/RoleAvatar.vue'
import type { Conversation, Role } from '@/types/api'

defineProps<{
  conv: Conversation
  role?: Role
  active?: boolean
  toggling?: boolean
  deleting?: boolean
  showStar?: boolean
}>()

defineEmits<{
  (e: 'open'): void
  (e: 'star', event: Event): void
  (e: 'rename', event: Event): void
  (e: 'delete', event: Event): void
}>()
</script>

<template>
  <div
    class="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-sidebar-hover"
    :class="active ? 'bg-sidebar-hover' : ''"
    role="button"
    tabindex="0"
    @click="$emit('open')"
    @keydown.enter="$emit('open')"
  >
    <!-- 头像 -->
    <RoleAvatar :avatar="role?.avatar" :name="role?.name" :size="28" />

    <!-- 标题 + 时间 -->
    <span class="flex flex-1 flex-col overflow-hidden">
      <span class="line-clamp-1 text-sm text-gray-700">{{ conv.title || '新对话' }}</span>
      <span class="text-[11px] text-gray-400">
        {{ formatRelativeTime(conv.last_message_at) || '尚未开始' }}
      </span>
    </span>

    <!-- 操作按钮 (hover 显示) -->
    <div class="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
      <!-- 收藏切换 (仅历史对话) -->
      <button
        v-if="showStar"
        type="button"
        class="rounded-full p-1 transition-colors hover:text-amber-400 active:text-amber-500"
        :class="conv.starred ? 'text-amber-400' : 'text-gray-300'"
        :disabled="toggling"
        :aria-label="conv.starred ? '取消收藏' : '收藏'"
        @click="$emit('star', $event)"
      >
        <StarSolidIcon v-if="conv.starred" class="h-4 w-4" :aria-hidden="true" />
        <StarOutlineIcon v-else class="h-4 w-4" :aria-hidden="true" />
      </button>

      <!-- 重命名 -->
      <button
        type="button"
        class="rounded-full p-1 text-gray-300 transition-colors hover:text-primary"
        aria-label="重命名"
        @click="$emit('rename', $event)"
      >
        <PencilIcon class="h-3.5 w-3.5" :aria-hidden="true" />
      </button>

      <!-- 取消收藏 (仅收藏夹列表) -->
      <button
        v-if="!showStar"
        type="button"
        class="rounded-full p-1 text-amber-400 active:text-amber-500"
        :disabled="toggling"
        aria-label="取消收藏"
        @click="$emit('star', $event)"
      >
        <StarSolidIcon class="h-4 w-4" :aria-hidden="true" />
      </button>

      <!-- 删除 -->
      <button
        type="button"
        class="rounded-full p-1 text-gray-300 transition-colors hover:text-red-500"
        :disabled="deleting"
        aria-label="删除"
        @click="$emit('delete', $event)"
      >
        <svg
          v-if="deleting"
          class="h-4 w-4 animate-spin text-gray-400"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="3"
          />
          <path
            class="opacity-90"
            fill="currentColor"
            d="M4 12a8 8 0 0 1 8-8v3a5 5 0 0 0-5 5H4z"
          />
        </svg>
        <TrashIcon v-else class="h-4 w-4" :aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
